import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import ColorPicker from "./ColorPicker";
import IconPicker from "./IconPicker";
import { DEFAULT_ICON } from "./icon-registry";
import { useModalDismiss } from "./useModalDismiss";

interface Folder {
    id: string;
    categoryId: string;
    name: string;
    description: string | null;
    icon: string | null;
    color: string | null;
    sortMode: string;
    isPinned: boolean;
    sortOrder: number;
}

interface Props {
    folder: Folder | null;
    categoryId: string;
    onSave: () => void;
    onCancel: () => void;
}

const DEFAULT_COLOR = "#64748B";

export default function FolderForm({ folder, categoryId, onSave, onCancel }: Props) {
    const isEdit = !!folder;
    const [name, setName] = useState(folder?.name || "");
    const [description, setDescription] = useState(folder?.description || "");
    const [icon, setIcon] = useState<string | null>(folder?.icon || DEFAULT_ICON);
    const [color, setColor] = useState(folder?.color || DEFAULT_COLOR);
    const [sortMode, setSortMode] = useState(folder?.sortMode || "last_modified");
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Name is required");
            return;
        }
        setSaving(true);

        const body = {
            name: name.trim(),
            description: description.trim() || null,
            icon,
            color,
            sortMode,
        };

        const request = isEdit
            ? axios.put(`/api/manuals/folders/${folder.id}`, {
                ...body,
                isPinned: folder.isPinned,
                sortOrder: folder.sortOrder,
            })
            : axios.post("/api/manuals/folders", {
                categoryId,
                ...body,
            });

        request
            .then(res => {
                if (res.data.success) {
                    toast.success(isEdit ? "Subfolder updated" : "Subfolder created");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to save");
                    setSaving(false);
                }
            })
            .catch(err => {
                const msg = err.response?.data?.message || "Failed to save folder";
                toast.error(msg);
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered scrollable backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>
                        {isEdit ? "Edit subfolder" : "Create new subfolder"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="m-label">Name</label>
                        <input
                            type="text"
                            className="m-input"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. Onboarding, Processes, etc."
                            maxLength={256}
                            autoFocus
                        />
                    </div>

                    <div className="mb-3">
                        <label className="m-label">Description (optional)</label>
                        <textarea
                            className="m-input"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Type your text..."
                            rows={2}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="m-label">Color</label>
                        <div><ColorPicker value={color} iconName={icon} onChange={setColor} /></div>
                    </div>

                    <div className="mb-3">
                        <label className="m-label">Icon</label>
                        <IconPicker value={icon} color={color} onChange={setIcon} />
                    </div>

                    <div className="mb-3">
                        <label className="m-label">What order should the items appear in?</label>
                        <select className="m-input" value={sortMode} onChange={e => setSortMode(e.target.value)}>
                            <option value="last_modified">last modified</option>
                            <option value="alphabetical">alphabetical</option>
                        </select>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? "Saving..." : isEdit ? "Update subfolder" : "Create subfolder"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
